import React, { useCallback, useEffect, useState } from 'react';
import { X, AlertTriangle, CheckCircle, Clock } from 'lucide-react';
import { Button } from './ui/Button';
import { trackAddToCart } from '../hooks/useMetaPixel';
export function ExitIntentPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const showPopup = useCallback(() => {
    if (!hasShown) {
      setIsVisible(true);
      setHasShown(true);
      document.body.style.overflow = 'hidden';
    }
  }, [hasShown]);
  const closePopup = () => {
    setIsVisible(false);
    document.body.style.overflow = '';
  };
  useEffect(() => {
    // Delay activation so it doesn't trigger immediately
    const activationTimer = setTimeout(() => {
      // Desktop: detect mouse leaving toward top of page
      const handleMouseLeave = (e: MouseEvent) => {
        if (e.clientY <= 5) {
          showPopup();
        }
      };
      document.addEventListener('mouseleave', handleMouseLeave);
      return () => {
        document.removeEventListener('mouseleave', handleMouseLeave);
      };
    }, 8000); // 8 second delay before activating
    return () => {
      clearTimeout(activationTimer);
      document.body.style.overflow = '';
    };
  }, [showPopup]);
  // Mobile: show after 30s of inactivity (scroll stop)
  useEffect(() => {
    let inactivityTimer: ReturnType<typeof setTimeout>;
    const resetTimer = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        if (window.innerWidth < 768) {
          showPopup();
        }
      }, 30000);
    };
    window.addEventListener('scroll', resetTimer);
    window.addEventListener('touchstart', resetTimer);
    resetTimer();
    return () => {
      clearTimeout(inactivityTimer);
      window.removeEventListener('scroll', resetTimer);
      window.removeEventListener('touchstart', resetTimer);
    };
  }, [showPopup]);
  if (!isVisible) return null;
  return <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-id="element-1">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" data-id="element-2" />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 fade-in duration-300" data-id="element-3">
        {/* Close button */}
        <button onClick={closePopup} className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors" data-id="element-4">
          <X className="w-5 h-5" data-id="element-5" />
        </button>

        {/* Top accent bar */}
        <div className="h-1.5 bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500" data-id="element-6" />

        {/* Content */}
        <div className="px-6 pt-8 pb-6 text-center" data-id="element-7">
          {/* Icon */}
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-5" data-id="element-8">
            <AlertTriangle className="w-8 h-8 text-red-500" data-id="element-9" />
          </div>

          <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-10">
            ¡Espera! No te vayas con las manos vacías.
          </h2>

          <p className="text-slate-600 mb-6 leading-relaxed" data-id="element-11">
            Entendemos que es una decisión importante. Por eso, te ofrecemos un
            <span className="font-semibold text-slate-900" data-id="element-12">
              {' '}
              descuento exclusivo{' '}
            </span>
            solo por hoy:
          </p>

          {/* Price comparison */}
          <div className="bg-slate-50 rounded-xl p-6 mb-6 border border-slate-100" data-id="element-13">
            <div className="flex items-center justify-center gap-4 mb-3" data-id="element-14">
              <div className="text-center" data-id="element-15">
                <p className="text-xs text-slate-400 uppercase tracking-wide mb-1" data-id="element-16">
                  Precio normal
                </p>
                <p className="text-2xl text-slate-400 line-through font-bold" data-id="element-17">
                  $19.900
                </p>
              </div>
              <div className="text-3xl text-slate-300" data-id="element-18">→</div>
              <div className="text-center" data-id="element-19">
                <p className="text-xs text-red-500 uppercase tracking-wide font-semibold mb-1" data-id="element-20">
                  Solo hoy
                </p>
                <p className="text-4xl text-slate-900 font-bold" data-id="element-21">$12.900</p>
              </div>
            </div>
            <p className="text-xs text-slate-500" data-id="element-22">
              COP · Pago único · Acceso inmediato
            </p>
          </div>

          {/* Benefits reminder */}
          <div className="space-y-2 mb-6 text-left" data-id="element-23">
            <div className="flex items-center gap-2 text-sm text-slate-700" data-id="element-24">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-25" />
              <span data-id="element-26">PDF completo + Plan día por día</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-700" data-id="element-27">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-28" />
              <span data-id="element-29">Plantillas Google Sheets listas para usar</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-700" data-id="element-30">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-31" />
              <span data-id="element-32">Scripts para WhatsApp y llamadas</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-700" data-id="element-33">
              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" data-id="element-34" />
              <span data-id="element-35">Garantía de 7 días</span>
            </div>
          </div>

          {/* Urgency */}
          <div className="flex items-center justify-center gap-2 text-sm text-orange-600 font-medium mb-5" data-id="element-36">
            <Clock className="w-4 h-4" data-id="element-37" />
            <span data-id="element-38">Esta oferta desaparece cuando cierres esta ventana</span>
          </div>

          {/* CTA */}
          <Button size="xl" fullWidth className="mb-3 bg-green-600 hover:bg-green-500 text-white border-0 shadow-lg shadow-green-900/20" onClick={() => {
          trackAddToCart();
          window.open('https://mapaclaro.com/cart/52341272543521:1', '_blank');
          closePopup();
        }} data-id="element-39">
            ¡Sí, quiero mi Mapa por $12.900!
          </Button>

          <button onClick={closePopup} className="text-sm text-slate-400 hover:text-slate-600 transition-colors" data-id="element-40">
            No gracias, prefiero seguir sin plan
          </button>
        </div>
      </div>
    </div>;
}